import { useEffect, useRef } from "react"
import { Comment } from "@/schema"

import { getCable } from "@/lib/actionCable"
import { useDevAuth as useAuth } from "@/hooks/use-dev-auth"

interface CommentCableCallbacks {
  onCommentsChanged?: () => void
  onCommentCreated: (comment: Comment) => void
  onCommentUpdated: (comment: Comment) => void
  onCommentDeleted: (commentId: string) => void
  onReactionAdded: (reaction: {
    id: string
    emoji: string
    reactable_type: string
    reactable_id: string
    user_id: string
  }) => void
  onReactionRemoved: (reaction: {
    id: string
    emoji: string
    reactable_type: string
    reactable_id: string
    user_id: string
  }) => void
}

export function useCommentCable(
  commentableType: string,
  commentableId: string,
  callbacks: CommentCableCallbacks,
  enabled: boolean = true
) {
  const { getToken } = useAuth()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const subscriptionRef = useRef<any>(null)

  useEffect(() => {
    if (!enabled || !commentableType || !commentableId) {
      return
    }

    const setupSubscription = async () => {
      const token = await getToken()
      if (!token) {
        return
      }

      const cable = getCable(token)

      subscriptionRef.current = cable.subscriptions.create(
        {
          channel: "CommentableChannel",
          type: commentableType,
          id: commentableId,
        },
        {
          connected(): void {
            // Connected to channel
          },
          disconnected(): void {
            // Disconnected from channel
          },
          rejected(): void {
            // Subscription rejected
          },
          received(data: {
            action: string
            comment?: Comment
            reaction?: { id: string; emoji: string; reactable_type: string; reactable_id: string; user_id: string }
          }): void {
            switch (data.action) {
              case "comments_changed":
                callbacks.onCommentsChanged?.()
                break
              case "comment_created":
                if (data.comment) {
                  callbacks.onCommentCreated(data.comment)
                }
                break
              case "comment_updated":
                if (data.comment) {
                  callbacks.onCommentUpdated(data.comment)
                }
                break
              case "comment_deleted":
                if (data.comment?.id) {
                  callbacks.onCommentDeleted(data.comment.id)
                }
                break
              case "reaction_added":
                if (data.reaction) {
                  callbacks.onReactionAdded(data.reaction)
                }
                break
              case "reaction_removed":
                if (data.reaction) {
                  callbacks.onReactionRemoved(data.reaction)
                }
                break
            }
          },
        }
      )
    }

    setupSubscription()

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
      }
    }
  }, [commentableType, commentableId, callbacks, enabled, getToken])
}
